import React, { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ProfileProgress() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Don't show anything until user data loads
  if (!user) return null; 

  // We assign a percentage "weight" to each field. Total = 100.
  const profileFields = [
    { key: "name", label: "Full Name", weight: 15 },
    { key: "phone", label: "Phone Number", weight: 15 },
    { key: "email", label: "Email Address", weight: 15 },
    { key: "isEmailVerified", label: "Email Verification", weight: 15 },
    { key: "avatar", label: "Profile Photo", weight: 10 },
    { key: "address", label: "Current Address", weight: 10 },
    { key: "gender", label: "Gender", weight: 10 },
    { key: "vehicle", label: "Vehicle Details", weight: 10 },
  ];

  let currentScore = 0;
  let missingFields = [];

  // Calculate the score
  profileFields.forEach((field) => {
    // Special check for avatar so default avatar doesn't count as complete
    if (field.key === "avatar") {
      if (user.avatar && !user.avatar.includes("default")) {
        currentScore += field.weight;
      } else {
        missingFields.push(field.label);
      }
    } 
    // Special check for booleans like isEmailVerified
    else if (field.key === "isEmailVerified") {
      if (user.isEmailVerified === true) currentScore += field.weight;
      else missingFields.push(field.label);
    } 
    // Normal string check for names, addresses, etc.
    else if (user[field.key] && user[field.key].toString().trim() !== "") {
      currentScore += field.weight;
    } else {
      missingFields.push(field.label);
    }
  });

  // Ensure it caps at 100
  const percentage = Math.min(currentScore, 100);

  // ✅ UI: If the profile is 100% complete, REMOVE it from the page
  if (percentage === 100) {
    return null;
  }

  // 🎨 CSS STYLES INJECTED DIRECTLY INTO THE COMPONENT
  const widgetStyles = `
    .progress-widget {
      background: linear-gradient(145deg, #ffffff, #f8f9fa);
      border: 1px solid #e3e8ee;
      border-radius: 16px;
      padding: 24px;
      margin-bottom: 30px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
      transition: transform 0.3s ease;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    .progress-widget:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.06);
    }
    .progress-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }
    .progress-header h3 {
      margin: 0 0 4px 0;
      font-size: 18px;
      font-weight: 800;
      color: #1a1f36;
    }
    .progress-header p {
      margin: 0;
      font-size: 13px;
      color: #697386;
    }
    .progress-percentage {
      font-size: 22px;
      font-weight: 800;
      color: #1a73e8;
      background: #e6f0fd;
      padding: 8px 16px;
      border-radius: 30px;
    }
    .progress-bar-bg {
      height: 10px;
      background: #e3e8ee;
      border-radius: 20px;
      overflow: hidden;
      margin-bottom: 20px;
    }
    .progress-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #1a73e8, #4bc0c8);
      border-radius: 20px;
      transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .progress-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-top: 1px solid #edf2f7;
      padding-top: 16px;
    }
    .missing-text {
      margin: 0;
      font-size: 13.5px;
      color: #4f566b;
    }
    .missing-text strong {
      color: #1a1f36;
    }
    .complete-now-btn {
      background: #1a73e8;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 13px;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .complete-now-btn:hover {
      background: #1557b0;
      transform: translateY(-1px);
    }
    @media (max-width: 600px) {
      .progress-footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
      }
      .complete-now-btn {
        width: 100%;
      }
    }
  `;

  // UI: If the profile is NOT complete
  return (
    <>
      <style>{widgetStyles}</style> 

      <div className="progress-widget">
        <div className="progress-header">
          <div>
            <h3>Complete your profile</h3>
            <p>Get more ride requests by finishing your setup.</p>
          </div>
          <div className="progress-percentage">
            {percentage}%
          </div>
        </div>

        <div className="progress-bar-bg">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${percentage}%` }}
          ></div>
        </div>

        <div className="progress-footer">
          <p className="missing-text">
            <strong>Next step:</strong> Add your {missingFields[0]}
          </p>
          <button className="complete-now-btn" onClick={() => navigate("/profile/edit")}>
            Complete Profile &rarr;
          </button>
        </div>
      </div>
    </>
  );
}